(() => {
  "use strict";

  const API_ROOT =
    "https://cover.mythinkcar.com/new_api/api/api/vehicleModelData";
  const DEFAULT_PRODUCT_NAME = "MUCAR 892BT Pro";
  const DEFAULT_PRODUCT_LABEL = "MUCAR 892BT PRO";
  // Coverage API returns an empty data set when size is greater than 100.
  const PAGE_SIZE = 100;
  // Fetch every page, but limit parallel requests to protect the API.
  const PAGE_FETCH_BATCH_SIZE = 4;
  const PAGE_RETRY_COUNT = 2;
  const GROUP_BATCH_SIZE = 8;

  function svgIcon(name) {
    const icons = {
      plus:
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
      check:
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4.2 4L19 6.8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    };
    return icons[name] || "";
  }

  function cleanSystemLabel(value) {
    const raw = String(value || "General")
      .replace(/\s+/g, " ")
      .trim();
    const withoutDatabaseCode = raw.replace(
      /^(?:0x)?(?:\d{2}|[A-F]\d|\d[A-F])(?=\s|[-–—:.)]|$)\s*(?:[-–—:.)]\s*)?/i,
      ""
    );
    const cleaned = withoutDatabaseCode.replace(/\s*[:：]\s*$/, "").trim();
    return cleaned || raw || "General";
  }

  function coverageRecordDetail(row, action) {
    return [
      row.subSystem && `Subsystem: ${row.subSystem}`,
      row.subFunction && row.subFunction !== action ? row.subFunction : "",
      row.engine && `Engine: ${row.engine}`,
      row.trimLevel && `Trim: ${row.trimLevel}`,
      row.bodyType && `Body: ${row.bodyType}`,
      row.succession && `Series: ${row.succession}`,
      row.chassis && `Chassis: ${row.chassis}`,
      row.department && `Department: ${row.department}`,
      row.region && `Region: ${row.region}`,
      row.connector && `Connector: ${row.connector}`,
      row.version && `Version: ${row.version}`
    ]
      .filter(Boolean)
      .join(" · ");
  }

  function completeCoverageRecordKey(row) {
    return [
      row.system,
      row.subSystem,
      row.function,
      row.subFunction,
      row.engine,
      row.trimLevel,
      row.bodyType,
      row.succession,
      row.chassis,
      row.department,
      row.region,
      row.connector,
      row.version
    ]
      .map((value) => String(value || "").trim())
      .join("::");
  }

  function uniqueSorted(values) {
    return [...new Set(values.filter(Boolean))].sort((a, b) =>
      String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: "base"
      })
    );
  }

  function fulfilledOrThrow(results) {
    const fulfilled = results
      .filter((result) => result.status === "fulfilled")
      .map((result) => result.value);

    if (fulfilled.length) return fulfilled;

    const firstFailure = results.find((result) => result.status === "rejected");
    throw firstFailure?.reason || new Error("Coverage sources are unavailable");
  }

  function yearSort(a, b) {
    const ay = Number(String(a).match(/\d{4}/)?.[0] || 0);
    const by = Number(String(b).match(/\d{4}/)?.[0] || 0);
    return by - ay || String(b).localeCompare(String(a));
  }

  function setOptions(select, items, placeholder, valueKey = null, labelKey = null) {
    select.replaceChildren();
    const prompt = document.createElement("option");
    prompt.value = "";
    prompt.textContent = placeholder;
    select.append(prompt);

    items.forEach((item) => {
      const option = document.createElement("option");
      option.value = valueKey ? item[valueKey] : item;
      option.textContent = labelKey ? item[labelKey] : item;
      select.append(option);
    });
  }

  function buildUrl(path, params) {
    const url = new URL(`${API_ROOT}/${path}`);
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, String(value));
      }
    });
    if (!url.searchParams.has("lang")) url.searchParams.set("lang", "EN");
    if (!url.searchParams.has("lan")) url.searchParams.set("lan", "EN");
    return url;
  }

  async function request(path, params, parentSignal) {
    const controller = new AbortController();
    let timeoutId;
    let parentAbort;

    if (parentSignal) {
      if (parentSignal.aborted) {
        controller.abort();
      } else {
        parentAbort = () => controller.abort();
        parentSignal.addEventListener("abort", parentAbort, { once: true });
      }
    }

    timeoutId = window.setTimeout(() => controller.abort(), 20000);

    try {
      const response = await fetch(buildUrl(path, params), {
        method: "GET",
        headers: { Accept: "application/json" },
        cache: "no-store",
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`Coverage service returned ${response.status}`);
      }

      return await response.json();
    } finally {
      window.clearTimeout(timeoutId);
      if (parentSignal && parentAbort) {
        parentSignal.removeEventListener("abort", parentAbort);
      }
    }
  }

  function pushAnalytics(eventName, productLabel, payload = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      product: productLabel,
      ...payload
    });
  }

  function init(root) {
    if (root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";
    root.dataset.coveragePageSize = String(PAGE_SIZE);

    const embedded = root.dataset.embedded === "true";
    const standalone = root.dataset.standalone === "true";
    const productName = root.dataset.productName || DEFAULT_PRODUCT_NAME;
    const isBt200Max = productName === "MUCAR BT200 MAX";
    const productLabel =
      root.dataset.productLabel || DEFAULT_PRODUCT_LABEL;
    const productUrl = root.dataset.productUrl || window.location.pathname;
    const variantId = root.dataset.variantId || "";
    const variantAvailable = root.dataset.variantAvailable === "true";
    const analyticsPrefix =
      root.dataset.analyticsPrefix || "892bt_pro";
    // COVERAGE_CANONICAL_689_892_20260820
    // Product management confirms THINKSCAN 689BT and MUCAR 892BT share
    // identical vehicle coverage. Use the richer audited 689BT directory as
    // their single canonical source so both pages cannot drift apart.
    const canonicalProductName =
      productName === "MUCAR 892BT" ? "THINKSCAN 689BT" : productName;
    const coverageProductNames = [canonicalProductName];
    root.dataset.coverageCanonicalProduct = canonicalProductName;
    const contactUrl = root.dataset.contactUrl || "/pages/contact";
    const overlay = root.querySelector("[data-m892-overlay]");
    const dialog = root.querySelector("[data-m892-dialog]");
    const opener = root.querySelector("[data-m892-open]");
    const closeButton = root.querySelector("[data-m892-close]");
    const form = root.querySelector("[data-m892-form]");
    const makeSelect = root.querySelector("[data-m892-make]");
    const modelSelect = root.querySelector("[data-m892-model]");
    const yearSelect = root.querySelector("[data-m892-year]");
    const makeMessage = root.querySelector("[data-m892-make-message]");
    const modelMessage = root.querySelector("[data-m892-model-message]");
    const yearMessage = root.querySelector("[data-m892-year-message]");
    const submitButton = root.querySelector("[data-m892-submit]");
    const retryButton = root.querySelector("[data-m892-retry]");
    const emptyState = root.querySelector('[data-m892-state="empty"]');
    const loadingState = root.querySelector('[data-m892-state="loading"]');
    const errorState = root.querySelector('[data-m892-state="error"]');
    const unsupportedState = root.querySelector(
      '[data-m892-state="unsupported"]'
    );
    const supportedState = root.querySelector(
      '[data-m892-state="supported"]'
    );
    const errorTitle = root.querySelector("[data-m892-error-title]");
    const errorCopy = root.querySelector("[data-m892-error-copy]");
    const unsupportedVehicle = root.querySelector(
      "[data-m892-unsupported-vehicle]"
    );
    const unsupportedContact = root.querySelector(
      "[data-m892-unsupported-contact]"
    );
    const unsupportedEdit = root.querySelector("[data-m892-unsupported-edit]");
    const vehicleTitle = root.querySelector("[data-m892-vehicle-title]");
    const systemsCount = root.querySelector("[data-m892-systems-count]");
    const functionsCount = root.querySelector("[data-m892-functions-count]");
    const recordsCount = root.querySelector("[data-m892-records-count]");
    const bidirectionalHighlight = root.querySelector(
      "[data-m892-bidirectional-highlight]"
    );
    const bidirectionalCount = root.querySelector(
      "[data-m892-bidirectional-count]"
    );
    const searchInput = root.querySelector("[data-m892-search]");
    const filters = root.querySelector("[data-m892-filters]");
    const matchCount = root.querySelector("[data-m892-match-count]");
    const groupsContainer = root.querySelector("[data-m892-groups]");
    const noMatches = root.querySelector("[data-m892-no-matches]");
    const loadMoreButton = root.querySelector("[data-m892-more]");
    const editButton = root.querySelector("[data-m892-edit]");
    const addToCartButton = root.querySelector("[data-m892-add-to-cart]");
    const cartButtonLabel = root.querySelector("[data-m892-cart-label]");
    const cartStatus = root.querySelector("[data-m892-cart-status]");
    const learnMoreLink = root.querySelector("[data-m892-learn-more]");
    const supportButton = root.querySelector("[data-m892-support-button]");
    const supportButtonLabel = root.querySelector("[data-m892-support-label]");
    const resultsPanel = root.querySelector("[data-m892-results]");
    const dialogMain = root.querySelector(".m892cov__dialog-main");

    if (!embedded && opener) {
      const productView = root.closest(
        ".productView-product, .product-page, .main-product"
      );
      const addToCart = productView?.querySelector(
        "#product-add-to-cart, [name='add'][type='submit'], .add_to_cart_button"
      );
      const purchaseGroup =
        addToCart?.closest(".x-block-buy-buttons") ||
        addToCart?.closest(".productView-group") ||
        addToCart?.closest(".productView-action") ||
        addToCart?.closest(".product-form__buttons");

      if (purchaseGroup?.parentElement) {
        const portal = document.createElement("div");
        portal.className = [
          ...root.classList,
          "m892cov__purchase-portal"
        ].join(" ");
        portal.dataset.m892CoveragePortal = "true";
        portal.dataset.productName = root.dataset.productName || "";
        productView?.classList.add("m892cov-product-purchase-flow");
        purchaseGroup.insertAdjacentElement("beforebegin", portal);
        portal.append(opener);
      }
    }

    let brands = [];
    let brandsLoaded = false;
    let brandsPromise = null;
    let makeMap = new Map();
    let lastFocused = null;
    let cascadeController = null;
    let resultController = null;
    let lastSearch = null;
    let allEntries = [];
    let activeCategory = "all";
    let visibleGroups = GROUP_BATCH_SIZE;
    let renderTimer = null;
    let autoSearchTimer = null;

    const stateElements = [
      emptyState,
      loadingState,
      errorState,
      unsupportedState,
      supportedState
    ];

    unsupportedContact.href = contactUrl;

    function showState(name) {
      stateElements.forEach((element) => {
        element.hidden = element.dataset.m892State !== name;
      });
    }

    function updateSubmit() {
      submitButton.disabled = !(
        makeSelect.value &&
        modelSelect.value &&
        yearSelect.value
      );
    }

    function cancelPendingSearch() {
      window.clearTimeout(autoSearchTimer);
      autoSearchTimer = null;
      resultController?.abort();
      resultController = null;
    }

    function eurusStore(name) {
      try {
        return window.Alpine?.store?.(name) || null;
      } catch (_) {
        return null;
      }
    }

    function setCartFeedback(message, isError = false) {
      if (!cartStatus) return;
      cartStatus.textContent = message;
      cartStatus.dataset.error = String(isError);
    }

    function openSupportChat() {
      if (!supportButton || supportButton.getAttribute("aria-busy") === "true") return;

      const defaultLabel = supportButtonLabel?.textContent || "Chat with Support";
      let attempts = 0;
      // SUPPORT_CHAT_CLOSE_MODAL_FIRST_20260817
      // The coverage dialog and the legacy Laykefu widget are both fixed,
      // body-level surfaces. Close the product modal before opening chat so
      // the customer never sees two competing overlays. Standalone coverage
      // pages remain in place because they are not modal surfaces.
      const shouldCloseCoverageModal =
        !embedded && !standalone && overlay && !overlay.hidden;

      supportButton.disabled = true;
      supportButton.classList.remove("is-unavailable");
      supportButton.setAttribute("aria-busy", "true");
      if (supportButtonLabel) supportButtonLabel.textContent = "Opening chat...";

      pushAnalytics(`${analyticsPrefix}_coverage_support`, productLabel, {
        coverage_surface: "product_modal"
      });

      const finish = () => {
        supportButton.disabled = false;
        supportButton.removeAttribute("aria-busy");
      };

      const focusMessage = () => {
        document.querySelector("#laykefu #msg-area")?.focus();
      };

      const tryOpen = () => {
        const panel = document.getElementById("laykefu");
        if (panel && window.getComputedStyle(panel).display !== "none") {
          focusMessage();
          if (supportButtonLabel) supportButtonLabel.textContent = defaultLabel;
          finish();
          return;
        }

        const launcher = document.getElementById("show-laykefu");
        if (launcher) {
          launcher.click();
          window.setTimeout(() => {
            focusMessage();
            if (supportButtonLabel) supportButtonLabel.textContent = defaultLabel;
            finish();
          }, 160);
          return;
        }

        attempts += 1;
        if (attempts < 20) {
          window.setTimeout(tryOpen, 150);
          return;
        }

        if (supportButtonLabel) supportButtonLabel.textContent = "Chat is loading - try again";
        supportButton.classList.add("is-unavailable");
        finish();
      };

      if (shouldCloseCoverageModal) {
        closeModal();
        window.setTimeout(tryOpen, 240);
      } else {
        tryOpen();
      }
    }

    async function addCoverageProductToCart() {
      if (!addToCartButton || !variantId || !variantAvailable) return;

      const originalLabel = cartButtonLabel?.textContent || "Add to Cart";
      addToCartButton.disabled = true;
      addToCartButton.dataset.loading = "true";
      if (cartButtonLabel) cartButtonLabel.textContent = "Adding...";
      setCartFeedback("");

      try {
        const cartHelper = eurusStore("xCartHelper");
        const sections = cartHelper
          ?.getSectionsToRender?.()
          ?.map((section) => section.id)
          .filter(Boolean) || [];
        const body = {
          items: [{ id: variantId, quantity: 1 }]
        };
        if (sections.length) body.sections = sections;

        const response = await fetch(
          `${window.Shopify?.routes?.root || "/"}cart/add.js`,
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json"
            },
            body: JSON.stringify(body)
          }
        );
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(
            payload?.description || payload?.message || "Unable to add this product"
          );
        }

        if (payload.sections && cartHelper?.reRenderSections) {
          cartHelper.reRenderSections(payload.sections);
        }
        document.dispatchEvent(
          new CustomEvent("eurus:cart:items-changed", { bubbles: true })
        );
        pushAnalytics(`${analyticsPrefix}_coverage_add_to_cart`, productLabel, {
          variant_id: variantId,
          coverage_surface: standalone ? "standalone_page" : "product_modal"
        });
        setCartFeedback("Added to cart.");
        if (cartButtonLabel) cartButtonLabel.textContent = "Added";
        addToCartButton.dataset.loading = "false";

        if (!embedded) closeModal();
        window.setTimeout(() => {
          const miniCart = eurusStore("xMiniCart");
          if (miniCart?.openCart) {
            miniCart.openCart();
          } else {
            window.location.assign(
              `${window.Shopify?.routes?.root || "/"}cart`
            );
          }
        }, embedded ? 0 : 210);
        window.setTimeout(() => {
          addToCartButton.disabled = false;
          if (cartButtonLabel) cartButtonLabel.textContent = originalLabel;
        }, 900);
      } catch (error) {
        setCartFeedback(
          error?.message || "The product could not be added. Please try again.",
          true
        );
        addToCartButton.disabled = false;
        addToCartButton.dataset.loading = "false";
        if (cartButtonLabel) cartButtonLabel.textContent = originalLabel;
      }
    }

    function scheduleAutoSearch() {
      window.clearTimeout(autoSearchTimer);
      autoSearchTimer = null;

      if (!makeSelect.value || !modelSelect.value || !yearSelect.value) {
        return;
      }

      autoSearchTimer = window.setTimeout(() => {
        autoSearchTimer = null;
        runSearch(null, "year_select");
      }, 250);
    }

    function clearMessages() {
      makeMessage.textContent = "";
      modelMessage.textContent = "";
      yearMessage.textContent = "";
    }

    function showFieldError(element, message) {
      element.textContent = message;
    }

    function selectedVehicle() {
      const make = makeMap.get(makeSelect.value);
      return {
        makeId: makeSelect.value,
        makeName:
          make?.name ||
          makeSelect.options[makeSelect.selectedIndex]?.textContent ||
          "",
        model: modelSelect.value,
        year: yearSelect.value,
        pdtType: make?.pdtType || 1,
        pdtAliasId: make?.pdtAliasId || 2,
        sources:
          make?.sources ||
          coverageProductNames.map(() => ({
            id: makeSelect.value,
            pdtType: make?.pdtType || 1,
            pdtAliasId: make?.pdtAliasId || 2,
            productName: canonicalProductName
          }))
      };
    }

    function vehicleLabel(vehicle) {
      return [vehicle.year, vehicle.makeName, vehicle.model]
        .filter(Boolean)
        .join(" ");
    }

    function resetResults() {
      delete root.dataset.hasResults;
      allEntries = [];
      activeCategory = "all";
      visibleGroups = GROUP_BATCH_SIZE;
      searchInput.value = "";
      groupsContainer.replaceChildren();
      filters.replaceChildren();
      if (bidirectionalHighlight) bidirectionalHighlight.hidden = true;
      delete root.dataset.bt200BidirectionalRecords;
      showState("empty");
    }

    async function loadBrands() {
      if (brandsLoaded) return brands;
      if (brandsPromise) return brandsPromise;

      setOptions(makeSelect, [], "Loading makes...");
      makeSelect.disabled = true;

      brandsPromise = request("selectModelList", {
        pdtType: 1,
        channel: 3,
        vehicleType: 2,
        productName: canonicalProductName
      })
        .then((payload) => {
          const makesById = new Map();

          (payload.result || [])
            .filter(
              (item) =>
                Boolean(item?.swId) &&
                Number(item.tableExists) !== 0 &&
                !/^RESET/i.test(item.swId) &&
                (item.vehicleType == null ||
                  Number(item.vehicleType) === 2)
            )
            .forEach((item) => {
              const id = String(item.swId || "").trim();
              const name = String(
                item.nameEn || item.name || item.nameCn || id
              ).trim();
              if (!id || !name) return;

              const key = id.toUpperCase();
              const source = {
                id,
                pdtType: Number(item.pdtType || 1),
                pdtAliasId: Number(item.pdtAliasId || 2),
                productName: canonicalProductName
              };
              const existing = makesById.get(key);

              if (existing) {
                if (name.length > existing.name.length) {
                  existing.name = name;
                }
                const sourceKey = `${source.pdtType}:${source.pdtAliasId}`;
                const hasSource = existing.sources.some(
                  (candidate) =>
                    `${candidate.pdtType}:${candidate.pdtAliasId}` ===
                    sourceKey
                );
                if (!hasSource) existing.sources.push(source);
                return;
              }

              makesById.set(key, {
                id,
                name,
                pdtType: source.pdtType,
                pdtAliasId: source.pdtAliasId,
                sources: [source]
              });
            });

          brands = [...makesById.values()].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, {
              sensitivity: "base"
            })
          );

          makeMap = new Map(brands.map((item) => [item.id, item]));
          setOptions(makeSelect, brands, "Select make", "id", "name");
          makeSelect.disabled = false;
          brandsLoaded = true;
          makeMessage.textContent = "";
          return brands;
        })
        .catch((error) => {
          setOptions(makeSelect, [], "Makes unavailable");
          makeSelect.disabled = false;
          makeMessage.textContent =
            error?.name === "AbortError"
              ? "Loading timed out. Close and try again."
              : "Vehicle makes could not be loaded. Try again.";
          throw error;
        })
        .finally(() => {
          brandsPromise = null;
        });

      return brandsPromise;
    }

    async function onMakeChange() {
      cancelPendingSearch();
      cascadeController?.abort();
      cascadeController = new AbortController();
      clearMessages();
      resetResults();

      setOptions(modelSelect, [], makeSelect.value ? "Loading models..." : "Select make first");
      setOptions(yearSelect, [], "Select model first");
      modelSelect.disabled = true;
      yearSelect.disabled = true;
      updateSubmit();

      if (!makeSelect.value) return;

      const make = makeMap.get(makeSelect.value);

      try {
        const sources =
          make?.sources ||
          coverageProductNames.map(() => ({
            id: makeSelect.value,
            pdtType: make?.pdtType || 1,
            pdtAliasId: make?.pdtAliasId || 2,
            productName: canonicalProductName
          }));
        const results = await Promise.allSettled(
          sources.map((source) =>
            request(
              "getModelSelect",
              {
                swId: source.id,
                pdtType: source.pdtType,
                pdtAliasId: source.pdtAliasId,
                type: 1
              },
              cascadeController.signal
            )
          )
        );
        const models = uniqueSorted(
          fulfilledOrThrow(results).flatMap((payload) => payload.data || [])
        );
        setOptions(modelSelect, models, "Select model");
        modelSelect.disabled = models.length === 0;
        if (!models.length) {
          showFieldError(
            modelMessage,
            `No ${productLabel} model records were returned for this make.`
          );
        }
      } catch (error) {
        if (error?.name !== "AbortError") {
          setOptions(modelSelect, [], "Models unavailable");
          showFieldError(modelMessage, "Models could not be loaded. Try the make again.");
        }
      } finally {
        updateSubmit();
      }
    }

    async function onModelChange() {
      cancelPendingSearch();
      cascadeController?.abort();
      cascadeController = new AbortController();
      modelMessage.textContent = "";
      yearMessage.textContent = "";
      resetResults();

      setOptions(
        yearSelect,
        [],
        modelSelect.value ? "Loading year ranges..." : "Select model first"
      );
      yearSelect.disabled = true;
      updateSubmit();

      if (!modelSelect.value) return;

      const make = makeMap.get(makeSelect.value);

      try {
        const sources =
          make?.sources ||
          coverageProductNames.map(() => ({
            id: makeSelect.value,
            pdtType: make?.pdtType || 1,
            pdtAliasId: make?.pdtAliasId || 2,
            productName: canonicalProductName
          }));
        const results = await Promise.allSettled(
          sources.map((source) =>
            request(
              "getModelSelect",
              {
                swId: source.id,
                pdtType: source.pdtType,
                pdtAliasId: source.pdtAliasId,
                type: 2,
                model: modelSelect.value
              },
              cascadeController.signal
            )
          )
        );
        const years = uniqueSorted(
          fulfilledOrThrow(results).flatMap((payload) => payload.data || [])
        ).sort(yearSort);
        setOptions(yearSelect, years, "Select year range");
        yearSelect.disabled = years.length === 0;
        if (!years.length) {
          showFieldError(
            yearMessage,
            `No ${productLabel} year records were returned for this model.`
          );
        }
      } catch (error) {
        if (error?.name !== "AbortError") {
          setOptions(yearSelect, [], "Year ranges unavailable");
          showFieldError(
            yearMessage,
            "Year ranges could not be loaded. Try the model again."
          );
        }
      } finally {
        updateSubmit();
      }
    }

    function customerFacingAction(value) {
      const action = String(value || "Supported").trim();
      if (!isBt200Max) return action;

      const normalized = action
        .toLowerCase()
        .replace(/[\s_-]+/g, " ")
        .trim();
      if (
        [
          "actuation test",
          "actuation tests",
          "active test",
          "active tests",
          "bidirectional test",
          "bidirectional tests",
          "bi directional test",
          "bi directional tests",
          "bi directional control"
        ].includes(normalized)
      ) {
        return "Bi-directional / Active Tests";
      }
      return action;
    }

    function normalizeRows(rows) {
      const deduped = new Map();

      rows
        .filter((row) => row && (row.system || row.function || row.subFunction))
        .forEach((row) => {
          const system = cleanSystemLabel(row.system);
          const rawAction = String(
            row.function || row.subFunction || "Supported"
          ).trim();
          const action = customerFacingAction(rawAction);
          const detail = coverageRecordDetail(row, rawAction);
          const entry = {
            system,
            action,
            detail
          };
          const key = completeCoverageRecordKey(row).toLowerCase();
          if (!deduped.has(key)) deduped.set(key, entry);
        });

      return [...deduped.values()].sort((a, b) => {
        const systemOrder = a.system.localeCompare(b.system, undefined, {
          numeric: true,
          sensitivity: "base"
        });
        if (systemOrder) return systemOrder;
        return a.action.localeCompare(b.action, undefined, {
          numeric: true,
          sensitivity: "base"
        });
      });
    }

    async function loadCoveragePage(
      baseParams,
      page,
      expectedRows,
      signal,
      confirmEmpty = false
    ) {
      let lastError;

      for (let attempt = 0; attempt <= PAGE_RETRY_COUNT; attempt += 1) {
        try {
          const payload = await request(
            "getModelPage",
            { ...baseParams, page },
            signal
          );
          const rows = Array.isArray(payload.result?.datas)
            ? payload.result.datas
            : [];
          const reportedTotal = Math.max(
            0,
            Number(payload.result?.total || 0)
          );
          const requiredRows =
            page === 1 && reportedTotal > 0
              ? Math.min(PAGE_SIZE, reportedTotal)
              : expectedRows;

          if (
            confirmEmpty &&
            rows.length === 0 &&
            attempt < PAGE_RETRY_COUNT
          ) {
            lastError = new Error(
              `Coverage page ${page} returned an empty result`
            );
            continue;
          }

          if (requiredRows > 0 && rows.length < requiredRows) {
            throw new Error(
              `Coverage page ${page} returned ${rows.length} of ${requiredRows} expected records`
            );
          }

          return { reportedTotal, rows };
        } catch (error) {
          if (error?.name === "AbortError" || signal?.aborted) throw error;
          lastError = error;
          if (attempt === PAGE_RETRY_COUNT) throw lastError;
        }
      }

      throw lastError || new Error(`Coverage page ${page} could not be loaded`);
    }

    async function loadCoverageSource(vehicle, source, signal) {
      const baseParams = {
        modelName: source.id,
        model: vehicle.model,
        modelYear: vehicle.year,
        pdtType: source.pdtType,
        pdtAliasId: source.pdtAliasId,
        pageSize: PAGE_SIZE,
        size: PAGE_SIZE
      };

      const firstPage = await loadCoveragePage(baseParams, 1, 0, signal);
      const reportedTotal = firstPage.reportedTotal;
      const planningTotal = Math.max(reportedTotal, firstPage.rows.length);
      const pageCount = Math.max(1, Math.ceil(planningTotal / PAGE_SIZE));
      const rowsByPage = new Map([[1, firstPage.rows]]);

      for (
        let firstPageNumber = 2;
        firstPageNumber <= pageCount;
        firstPageNumber += PAGE_FETCH_BATCH_SIZE
      ) {
        const pages = Array.from(
          {
            length: Math.min(
              PAGE_FETCH_BATCH_SIZE,
              pageCount - firstPageNumber + 1
            )
          },
          (_, index) => firstPageNumber + index
        );
        const pageResults = await Promise.all(
          pages.map((page) => {
            const remainingRows = planningTotal - (page - 1) * PAGE_SIZE;
            const expectedRows = Math.min(PAGE_SIZE, remainingRows);
            return loadCoveragePage(
              baseParams,
              page,
              expectedRows,
              signal
            );
          })
        );

        pages.forEach((page, index) => {
          rowsByPage.set(page, pageResults[index].rows);
        });
      }

      /*
       * `total` is useful for planning, but it is not trusted as the end of
       * the data. When the planned last page is full, probe subsequent pages
       * until the API returns a short page. This recovers records when total
       * is zero or underreported.
       */
      let lastPageNumber = pageCount;
      let lastPageRows = rowsByPage.get(lastPageNumber) || [];
      const seenFullPageSignatures = new Set();

      rowsByPage.forEach((pageRows) => {
        if (pageRows.length === PAGE_SIZE) {
          seenFullPageSignatures.add(
            pageRows
              .map(
                (row) =>
                  row?.id ??
                  `${row?.system || ""}::${row?.function || ""}::${row?.subFunction || ""}`
              )
              .join("|")
          );
        }
      });

      while (lastPageRows.length === PAGE_SIZE) {
        const nextPageNumber = lastPageNumber + 1;
        const nextPage = await loadCoveragePage(
          baseParams,
          nextPageNumber,
          0,
          signal,
          true
        );
        const nextSignature = nextPage.rows
          .map(
            (row) =>
              row?.id ??
              `${row?.system || ""}::${row?.function || ""}::${row?.subFunction || ""}`
          )
          .join("|");

        if (
          nextPage.rows.length === PAGE_SIZE &&
          seenFullPageSignatures.has(nextSignature)
        ) {
          break;
        }

        rowsByPage.set(nextPageNumber, nextPage.rows);
        if (nextPage.rows.length === PAGE_SIZE) {
          seenFullPageSignatures.add(nextSignature);
        }
        lastPageNumber = nextPageNumber;
        lastPageRows = nextPage.rows;
      }

      const rows = [...rowsByPage.entries()]
        .sort(([pageA], [pageB]) => pageA - pageB)
        .flatMap(([, pageRows]) => pageRows);
      const resolvedTotal = Math.max(reportedTotal, rows.length);

      if (reportedTotal > 0 && rows.length < reportedTotal) {
        throw new Error(
          `Coverage pagination returned ${rows.length} of ${reportedTotal} records`
        );
      }

      return {
        total: resolvedTotal,
        rows,
        truncated: false,
        pagesFetched: rowsByPage.size,
        productName: source.productName,
        pdtType: source.pdtType,
        pdtAliasId: source.pdtAliasId
      };
    }

    async function loadCoverage(vehicle, signal) {
      const sources =
        vehicle.sources ||
        coverageProductNames.map(() => ({
          id: vehicle.makeId,
          pdtType: vehicle.pdtType,
          pdtAliasId: vehicle.pdtAliasId,
          productName: canonicalProductName
        }));
      const sourceResults = fulfilledOrThrow(
        await Promise.allSettled(
          sources.map((source) => loadCoverageSource(vehicle, source, signal))
        )
      );
      const rows = sourceResults.flatMap((result) => result.rows);
      const uniqueRecordCount = normalizeRows(rows).length;
      const sourceChecks = sourceResults.map((result) => {
        const entries = normalizeRows(result.rows);
        return {
          productName: result.productName,
          pdtType: result.pdtType,
          pdtAliasId: result.pdtAliasId,
          sourceRecordCount: result.total,
          functionCount: entries.length,
          signature: entries
            .map(
              (entry) =>
                `${entry.system}::${entry.action}::${entry.detail}`.toLowerCase()
            )
            .sort()
            .join("|")
        };
      });
      const sourceCounts = sourceChecks.map(
        ({
          productName: sourceName,
          pdtType,
          pdtAliasId,
          sourceRecordCount,
          functionCount
        }) => ({
          productName: sourceName,
          pdtType,
          pdtAliasId,
          sourceRecordCount,
          functionCount
        })
      );
      const sourceSignatures = new Set(
        sourceChecks.map((source) => source.signature)
      );

      return {
        total: sourceResults.reduce(
          (sum, result) => sum + result.total,
          0
        ),
        uniqueRecordCount,
        rows,
        truncated: sourceResults.some((result) => result.truncated),
        pagesFetched: sourceResults.reduce(
          (sum, result) => sum + result.pagesFetched,
          0
        ),
        sourceCounts,
        mappingsMerged: sourceResults.length > 1,
        mappingSignaturesDiffer: sourceSignatures.size > 1
      };
    }

    function createFilter(value, labelText, count) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "m892cov__filter";
      button.dataset.category = value;
      button.dataset.active = String(activeCategory === value);
      button.setAttribute(
        "aria-pressed",
        String(activeCategory === value)
      );
      button.setAttribute(
        "aria-label",
        `${labelText}: ${count} ${count === 1 ? "function" : "functions"}`
      );

      const label = document.createElement("span");
      label.textContent = labelText;
      const number = document.createElement("span");
      number.className = "m892cov__filter-count";
      number.textContent = String(count);
      button.append(label, number);
      return button;
    }

    function renderFilters() {
      const counts = new Map();

      allEntries.forEach((entry) => {
        counts.set(entry.action, (counts.get(entry.action) || 0) + 1);
      });

      filters.replaceChildren();
      filters.append(createFilter("all", "All functions", allEntries.length));
      [...counts.entries()]
        .sort(
          ([actionA, countA], [actionB, countB]) =>
            countB - countA ||
            actionA.localeCompare(actionB, undefined, {
              sensitivity: "base"
            })
        )
        .forEach(([action, count]) => {
          filters.append(createFilter(action, action, count));
        });
      filters.dataset.count = String(filters.children.length);
    }

    function createFunctionItem(entry) {
      const item = document.createElement("li");
      item.className = "m892cov__function";

      const dot = document.createElement("span");
      dot.className = "m892cov__function-dot";
      dot.setAttribute("aria-hidden", "true");

      const copy = document.createElement("span");
      copy.className = "m892cov__function-copy";
      const action = document.createElement("strong");
      action.textContent = entry.action;
      copy.append(action);
      if (entry.detail) {
        const detail = document.createElement("span");
        detail.textContent = entry.detail;
        copy.append(detail);
      }

      item.append(dot, copy);
      return item;
    }

    function createGroup(system, entries, index) {
      const details = document.createElement("details");
      details.className = "m892cov__group";
      details.open = index === 0 && !searchInput.value.trim();

      const summary = document.createElement("summary");
      const name = document.createElement("span");
      name.className = "m892cov__group-name";
      name.textContent = system;
      const count = document.createElement("span");
      count.className = "m892cov__group-count";
      count.textContent = `${entries.length} ${
        entries.length === 1 ? "function" : "functions"
      }`;
      const icon = document.createElement("span");
      icon.className = "m892cov__group-chevron";
      icon.setAttribute("aria-hidden", "true");
      icon.innerHTML = svgIcon("plus");
      summary.append(name, count, icon);

      const list = document.createElement("ul");
      list.className = "m892cov__function-list";
      entries.forEach((entry) => list.append(createFunctionItem(entry)));

      details.append(summary, list);
      return details;
    }

    function renderGroups() {
      const query = searchInput.value.trim().toLowerCase();
      const filtered = allEntries.filter((entry) => {
        const categoryMatch =
          activeCategory === "all" || entry.action === activeCategory;
        if (!categoryMatch) return false;
        if (!query) return true;
        return `${entry.system} ${entry.action} ${entry.detail}`
          .toLowerCase()
          .includes(query);
      });

      const grouped = new Map();
      filtered.forEach((entry) => {
        if (!grouped.has(entry.system)) grouped.set(entry.system, []);
        grouped.get(entry.system).push(entry);
      });

      const groupRows = [...grouped.entries()];
      const visibleRows = groupRows.slice(0, visibleGroups);

      groupsContainer.replaceChildren();
      visibleRows.forEach(([system, entries], index) => {
        groupsContainer.append(createGroup(system, entries, index));
      });

      matchCount.textContent = `${filtered.length} ${
        filtered.length === 1 ? "function" : "functions"
      } across ${groupRows.length} ${
        groupRows.length === 1 ? "vehicle system" : "vehicle systems"
      }`;
      noMatches.hidden = filtered.length !== 0;
      loadMoreButton.hidden = visibleRows.length >= groupRows.length;

      if (!loadMoreButton.hidden) {
        loadMoreButton.textContent = `Show all remaining systems (${groupRows.length - visibleRows.length})`;
      }
    }

    function renderSupported(vehicle, coverage) {
      allEntries = normalizeRows(coverage.rows);
      root.dataset.coverageSelfCheck =
        "exact-product-source-pagination-complete";
      root.dataset.coveragePagesFetched = String(
        coverage.pagesFetched || 0
      );
      root.dataset.coverageSourceRecords = String(coverage.total || 0);
      root.dataset.coverageUniqueRecords = String(allEntries.length);

      if (!coverage.total || allEntries.length === 0) {
        renderUnsupported(vehicle);
        return;
      }

      const systems = new Set(allEntries.map((entry) => entry.system));
      const functions = new Set(allEntries.map((entry) => entry.action));
      const bidirectionalRecords = allEntries.filter(
        (entry) => entry.action === "Bi-directional / Active Tests"
      ).length;

      if (bidirectionalHighlight) {
        bidirectionalHighlight.hidden = !(
          isBt200Max && bidirectionalRecords > 0
        );
      }
      if (bidirectionalCount) {
        bidirectionalCount.textContent = String(bidirectionalRecords);
      }
      if (isBt200Max) {
        root.dataset.bt200BidirectionalRecords = String(
          bidirectionalRecords
        );
      }

      vehicleTitle.textContent = vehicleLabel(vehicle);
      systemsCount.textContent = String(systems.size);
      functionsCount.textContent = String(functions.size);
      recordsCount.textContent = String(coverage.uniqueRecordCount);
      searchInput.value = "";
      activeCategory = "all";
      visibleGroups = GROUP_BATCH_SIZE;
      renderFilters();
      renderGroups();
      showState("supported");
      root.dataset.hasResults = "true";
      window.requestAnimationFrame(() => {
        dialogMain?.scrollTo({
          top: 0,
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ? "auto"
            : "smooth"
        });
      });
      pushAnalytics(`${analyticsPrefix}_coverage_result`, productLabel, {
        supported: true,
        exact_coverage_product: productName,
        canonical_coverage_product: canonicalProductName,
        product_mapping_counts: coverage.sourceCounts,
        coverage_pages_fetched: coverage.pagesFetched
      });
    }

    function renderUnsupported(vehicle) {
      delete root.dataset.hasResults;
      unsupportedVehicle.textContent = vehicleLabel(vehicle);
      showState("unsupported");
      pushAnalytics(`${analyticsPrefix}_coverage_result`, productLabel, {
        supported: false
      });
    }

    async function runSearch(event, trigger = "form_submit") {
      event?.preventDefault();
      clearMessages();

      if (!makeSelect.value || !modelSelect.value || !yearSelect.value) {
        if (!makeSelect.value) {
          showFieldError(makeMessage, "Select a vehicle make.");
          makeSelect.focus();
        } else if (!modelSelect.value) {
          showFieldError(modelMessage, "Select a vehicle model.");
          modelSelect.focus();
        } else {
          showFieldError(yearMessage, "Select a year range.");
          yearSelect.focus();
        }
        return;
      }

      window.clearTimeout(autoSearchTimer);
      autoSearchTimer = null;
      resultController?.abort();
      const controller = new AbortController();
      resultController = controller;
      const search = selectedVehicle();
      lastSearch = search;
      showState("loading");
      submitButton.disabled = true;
      pushAnalytics(
        `${analyticsPrefix}_coverage_submit`,
        productLabel,
        { coverage_trigger: trigger }
      );

      if (window.matchMedia("(max-width: 820px)").matches) {
        resultsPanel.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ? "auto"
            : "smooth",
          block: "start"
        });
      }

      try {
        const coverage = await loadCoverage(
          search,
          controller.signal
        );
        if (controller !== resultController || controller.signal.aborted) {
          return;
        }
        renderSupported(search, coverage);
      } catch (error) {
        if (controller !== resultController || controller.signal.aborted) {
          return;
        }
        if (error?.name === "AbortError") {
          errorTitle.textContent = "The coverage check timed out";
          errorCopy.textContent =
            "The coverage database took too long to respond. Try the same vehicle again.";
        } else {
          errorTitle.textContent = "Coverage is temporarily unavailable";
          errorCopy.textContent =
            "Your vehicle details are still selected. Try again in a moment.";
        }
        showState("error");
      } finally {
        if (controller === resultController) {
          resultController = null;
          updateSubmit();
        }
      }
    }

    function setModalVisualState(isOpen) {
      overlay.dataset.open = isOpen ? "true" : "false";
      overlay.style.setProperty(
        "opacity",
        isOpen ? "1" : "0",
        "important"
      );
      overlay.style.setProperty(
        "pointer-events",
        isOpen ? "auto" : "none",
        "important"
      );
      if (isOpen) {
        overlay.style.setProperty("visibility", "visible", "important");
      }
      dialog.style.setProperty(
        "opacity",
        isOpen ? "1" : "0",
        "important"
      );
      dialog.style.setProperty(
        "transform",
        isOpen
          ? "translate3d(0, 0, 0) scale(1)"
          : "translate3d(0, 32px, 0) scale(0.985)",
        "important"
      );
    }

    // CONCEPT_VIEWPORT_MODAL_PORTAL_20260810
    let modalPortal = null;

    function mountOverlayToViewport() {
      if (embedded || standalone || !overlay) return;
      if (!modalPortal) {
        modalPortal = document.createElement("div");
        modalPortal.className = [
          ...root.classList,
          "m892cov__modal-portal"
        ].join(" ");
        modalPortal.dataset.m892CoveragePortal = "modal";
        document.body.appendChild(modalPortal);
      }
      if (overlay.parentElement !== modalPortal) {
        modalPortal.appendChild(overlay);
      }
    }

    async function openModal() {
      if (embedded) return;
      mountOverlayToViewport();
      lastFocused = document.activeElement;
      // First lazy load temporarily disables the trigger and can blur it.
      if (
        (productName === "THINKSCAN 673BT" || productName === "MUCAR 783BT") &&
        lastFocused === document.body && opener
      ) {
        lastFocused = opener;
      }
      overlay.hidden = false;
      document.documentElement.classList.add("m892cov-lock");
      // Do not rely on requestAnimationFrame here. Mobile browsers can pause
      // animation frames in low-power or background states, which would leave
      // the page locked while the modal remains transparent.
      overlay.getBoundingClientRect();
      setModalVisualState(true);
      closeButton.focus({ preventScroll: true });
      pushAnalytics(
        `${analyticsPrefix}_coverage_open`,
        productLabel
      );
      try {
        await loadBrands();
        if (
          makeSelect.value &&
          modelSelect.value &&
          yearSelect.value &&
          !root.dataset.hasResults
        ) {
          scheduleAutoSearch();
        }
      } catch (_) {
        // The field shows the actionable error.
      }
    }

    function closeModal() {
      if (embedded || overlay.hidden) return;
      const wasLoading = !loadingState.hidden;
      cancelPendingSearch();
      if (wasLoading) resetResults();
      setModalVisualState(false);
      document.documentElement.classList.remove("m892cov-lock");
      const finalizeClose = () => {
        overlay.hidden = true;
        overlay.style.setProperty("visibility", "hidden", "important");
        lastFocused?.focus?.({ preventScroll: true });
      };
      if (document.visibilityState === "hidden") {
        finalizeClose();
      } else {
        window.setTimeout(finalizeClose, 190);
      }
    }

    function trapFocus(event) {
      if (embedded || overlay.hidden || event.key !== "Tab") return;
      const focusable = [
        ...dialog.querySelectorAll(
          'button:not([disabled]), select:not([disabled]), input:not([disabled]), a[href], summary, [tabindex]:not([tabindex="-1"])'
        )
      ].filter(
        (element) =>
          !element.hidden &&
          element.offsetParent !== null &&
          element.getAttribute("aria-hidden") !== "true"
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    opener?.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      openModal();
    });

    closeButton?.addEventListener("click", closeModal);

    overlay?.addEventListener("mousedown", (event) => {
      if (event.target === overlay) closeModal();
    });

    dialog.addEventListener("mousedown", (event) => event.stopPropagation());

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !embedded && !overlay.hidden) {
        event.preventDefault();
        closeModal();
      } else {
        trapFocus(event);
      }
    });

    makeSelect.addEventListener("change", onMakeChange);
    modelSelect.addEventListener("change", onModelChange);
    yearSelect.addEventListener("change", () => {
      cancelPendingSearch();
      yearMessage.textContent = "";
      resetResults();
      updateSubmit();
      scheduleAutoSearch();
    });
    form.addEventListener("submit", (event) => {
      runSearch(event, "form_submit");
    });
    retryButton.addEventListener("click", () => runSearch(null, "retry"));
    unsupportedEdit.addEventListener("click", () => {
      cancelPendingSearch();
      delete root.dataset.hasResults;
      showState("empty");
      makeSelect.focus({ preventScroll: true });
      makeSelect.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    editButton.addEventListener("click", () => {
      cancelPendingSearch();
      delete root.dataset.hasResults;
      showState("empty");
      makeSelect.focus({ preventScroll: true });
      makeSelect.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    addToCartButton?.addEventListener("click", addCoverageProductToCart);
    supportButton?.addEventListener("click", openSupportChat);
    learnMoreLink?.addEventListener("click", () => {
      pushAnalytics(`${analyticsPrefix}_coverage_learn_more`, productLabel, {
        product_url: productUrl,
        coverage_surface: standalone ? "standalone_page" : "product_modal"
      });
    });

    filters.addEventListener("click", (event) => {
      const button = event.target.closest("[data-category]");
      if (!button) return;
      activeCategory = button.dataset.category;
      visibleGroups = GROUP_BATCH_SIZE;
      [...filters.querySelectorAll("[data-category]")].forEach((item) => {
        const active = item.dataset.category === activeCategory;
        item.dataset.active = String(active);
        item.setAttribute("aria-pressed", String(active));
      });
      renderGroups();
    });

    searchInput.addEventListener("input", () => {
      window.clearTimeout(renderTimer);
      renderTimer = window.setTimeout(() => {
        visibleGroups = GROUP_BATCH_SIZE;
        renderGroups();
      }, 120);
    });

    loadMoreButton.addEventListener("click", () => {
      visibleGroups = Number.MAX_SAFE_INTEGER;
      renderGroups();
    });

    setOptions(makeSelect, [], "Select make");
    setOptions(modelSelect, [], "Select make first");
    setOptions(yearSelect, [], "Select model first");
    modelSelect.disabled = true;
    yearSelect.disabled = true;
    updateSubmit();
    showState("empty");

    if (embedded) {
      loadBrands().catch(() => {
        // The field shows the actionable error.
      });
    }
  }

  function boot() {
    document
      .querySelectorAll("[data-m892-coverage]")
      .forEach((root) => init(root));
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener("m892cov:mounted", boot);
  document.addEventListener("shopify:section:load", boot);
})();
